import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Building2, Boxes, ArrowLeftRight, Network, Fuel, Container, Share2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../data/site";
import { Reveal, SectionTag } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { RouteMap } from "../components/RouteMap";

const DIFF_ICONS = [MapPin, Building2, Boxes, ArrowLeftRight, Network, Fuel, Container, Share2];

const Home = () => {
  const { t } = useLanguage();
  const h = t.home;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  const opsImages = [IMAGES.lithoBulk, IMAGES.stsOperation, IMAGES.terminalTop, IMAGES.bunkerDeck, IMAGES.containerShip, IMAGES.terminalOverview];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative pt-16 pb-20 px-6 lg:px-12 border-b border-ops overflow-hidden">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="space-y-5">
            <SectionTag>{h.heroChip}</SectionTag>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-[76px] font-extrabold uppercase leading-[0.95] text-white tracking-tight">
              <span className="line-up"><span>{h.heroTitleA}</span></span>
              <span className="line-up line-up-delay"><span className="text-ops-light">{h.heroTitleB}</span></span>
            </h1>
            <p className="text-base sm:text-lg text-ops-muted max-w-2xl leading-relaxed">{h.heroSub}</p>
            <p className="font-display text-sm sm:text-base font-bold uppercase tracking-widest text-white border-l-2 border-ops-light pl-4">
              {h.heroSingular}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/terminal"
                data-testid="hero-cta-terminal"
                className="px-6 py-3.5 bg-ops-light text-ops-base text-xs font-display font-bold uppercase tracking-widest rounded hover:bg-white transition-colors inline-flex items-center gap-2"
              >
                {h.ctaTerminal} <ArrowRight size={14} />
              </Link>
              <Link
                to="/operacoes"
                data-testid="hero-cta-operations"
                className="px-6 py-3.5 border border-ops text-white text-xs font-display font-bold uppercase tracking-widest rounded hover:border-ops-light hover:text-ops-light transition-colors"
              >
                {h.ctaOperations}
              </Link>
            </div>
          </div>

          {/* Set-piece visual */}
          <div data-testid="hero-visual" className="relative rounded-2xl overflow-hidden border border-ops aspect-[21/9] band-slide bg-ops-panel">
            <motion.img
              src={IMAGES.aerialWide}
              alt="TUP Open Port — vista aérea do cais com embarcação atracada"
              style={{ y: imgY, objectPosition: "center 62%" }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ops-base via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
              <div className="p-4 rounded bg-ops-base/85 border border-ops backdrop-blur-sm max-w-md">
                <span className="text-[10px] mono-tag text-ops-light uppercase tracking-widest">{h.heroCardTag}</span>
                <p className="text-sm font-display font-semibold text-white mt-1">{h.heroCardText}</p>
              </div>
              <Link
                to="/contato"
                data-testid="hero-cta-presentation"
                className="px-6 py-3 bg-ops-light text-ops-base text-xs font-display font-bold uppercase tracking-wider rounded hover:bg-white transition-colors"
              >
                {h.heroBtn}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={h.marquee} />

      {/* LOCALIZAÇÃO */}
      <section data-testid="location-section" className="py-24 px-6 lg:px-12 border-b border-ops">
        <div className="max-w-7xl mx-auto space-y-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <Reveal className="lg:col-span-7 space-y-4">
              <SectionTag>{h.locTag}</SectionTag>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white leading-tight">{h.locTitle}</h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <p className="text-sm text-ops-muted leading-relaxed">{h.locText}</p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { src: IMAGES.mapMaranhao, cap: h.locCapState, testid: "map-state" },
              { src: IMAGES.mapSatellite, cap: h.locCapSat, testid: "map-satellite" },
              { src: IMAGES.mapBay, cap: h.locCapBay, testid: "map-bay" },
            ].map((m, i) => (
              <Reveal key={m.testid} delay={i * 0.12}>
                <figure className="group rounded-xl overflow-hidden border border-ops bg-ops-surface">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={m.src}
                      alt={m.cap}
                      data-testid={m.testid}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <figcaption className="px-4 py-3 text-[11px] mono-tag uppercase tracking-widest text-ops-muted border-t border-ops">
                    {m.cap}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section data-testid="differentials-section" className="py-24 px-6 lg:px-12 border-b border-ops bg-ops-panel/60">
        <div className="max-w-7xl mx-auto space-y-14">
          <div className="max-w-3xl space-y-4">
            <SectionTag>{h.diffTag}</SectionTag>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white leading-tight">{h.diffTitle}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm text-ops-muted leading-relaxed">{h.diffText}</p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {h.diffItems.map((item, i) => {
              const Icon = DIFF_ICONS[i % DIFF_ICONS.length];
              return (
                <Reveal key={item} delay={i * 0.06}>
                  <div
                    data-testid={`diff-item-${i}`}
                    className="group p-6 rounded-xl border border-ops bg-ops-surface hover:border-ops-light/60 hover:bg-ops-core/30 transition-colors h-full"
                  >
                    <Icon size={22} className="text-ops-light mb-4 group-hover:scale-110 transition-transform" />
                    <span className="font-display text-sm font-bold uppercase tracking-wide text-white">{item}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OPERAÇÕES */}
      <section data-testid="operations-section" className="py-24 px-6 lg:px-12 border-b border-ops">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-3">
              <SectionTag>{h.opsTag}</SectionTag>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white">{h.opsTitle}</h2>
            </div>
            <p className="text-xs text-ops-muted max-w-xs">{h.opsText}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {h.opsCards.map((card, i) => (
              <Reveal key={card.title} delay={(i % 3) * 0.1}>
                <article
                  data-testid={`ops-card-${i}`}
                  className="group p-6 rounded-xl border border-ops bg-ops-surface space-y-5 h-full flex flex-col hover:border-ops-light/60 transition-colors"
                >
                  <div className="aspect-[16/10] rounded overflow-hidden border border-ops">
                    <img
                      src={opsImages[i]}
                      alt={card.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <span className="text-[10px] mono-tag text-ops-light uppercase tracking-widest">{card.tag}</span>
                  <h3 className="font-display text-xl font-bold text-white">{card.title}</h3>
                  <p className="text-xs text-ops-muted leading-relaxed flex-1">{card.desc}</p>
                  <Link
                    to="/operacoes"
                    data-testid={`ops-card-link-${i}`}
                    className="inline-flex items-center gap-2 text-[11px] mono-tag uppercase tracking-widest text-ops-light hover:text-white transition-colors"
                  >
                    {h.learnMore} <ArrowRight size={12} />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LITHOTHAMNIUM / BMO */}
      <section data-testid="bmo-section" className="py-24 px-6 lg:px-12 border-b border-ops bg-ops-panel">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <SectionTag>{h.bmoTag}</SectionTag>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white leading-tight">{h.bmoTitle}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm text-ops-muted leading-relaxed">{h.bmoText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="p-6 rounded-xl border border-ops bg-ops-core/20 space-y-2">
                <span className="text-xs font-display font-bold text-white uppercase tracking-wide">{h.bmoBoxTitle}</span>
                <p className="text-xs text-ops-muted leading-relaxed">{h.bmoBoxText}</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-ops aspect-[4/3]">
              <img src={IMAGES.lithoBulk} alt="Lithothamnium — granulado claro" data-testid="bmo-image" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ops-base via-ops-base/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded bg-ops-base/85 border border-ops backdrop-blur-sm">
                <span className="text-[10px] mono-tag text-ops-light uppercase tracking-widest block">{h.bmoTag}</span>
                <div className="text-sm font-display font-bold text-white mt-1">{h.bmoBadge}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CABOTAGEM */}
      <section data-testid="cabotage-section" className="py-24 px-6 lg:px-12 border-b border-ops">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 space-y-6">
            <SectionTag>{h.cabTag}</SectionTag>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white leading-tight">{h.cabTitle}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm text-ops-muted leading-relaxed">{h.cabText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/cabotagem"
                data-testid="cabotage-cta"
                className="inline-flex items-center gap-2 px-6 py-3 border border-ops-light text-ops-light text-xs font-display font-bold uppercase tracking-widest rounded hover:bg-ops-light hover:text-ops-base transition-colors"
              >
                {h.cabBtn} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <RouteMap compact />
          </Reveal>
        </div>
      </section>

      {/* BUNKER */}
      <section data-testid="bunker-section" className="py-24 px-6 lg:px-12 border-b border-ops bg-ops-panel/60">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl overflow-hidden border border-ops aspect-[3/4]">
                <img src={IMAGES.bunkerDeck} alt="Operação técnica de bunker — tubulações e convés" data-testid="bunker-image-deck" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="rounded-xl overflow-hidden border border-ops aspect-[3/4] mt-10">
                <img src={IMAGES.vesselSea} alt="Embarcação em operação no mar" data-testid="bunker-image-vessel" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
            <SectionTag>{h.bunkTag}</SectionTag>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase text-white leading-tight">{h.bunkTitle}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm text-ops-muted leading-relaxed">{h.bunkText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/operacoes"
                data-testid="bunker-cta"
                className="inline-flex items-center gap-2 text-[11px] mono-tag uppercase tracking-widest text-ops-light hover:text-white transition-colors"
              >
                {h.bunkBtn} <ArrowRight size={12} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section data-testid="final-cta-section" className="relative py-28 px-6 lg:px-12 overflow-hidden">
        <div
          className="absolute inset-0 drift-ambient"
          style={{
            background:
              "radial-gradient(90% 80% at 80% 20%, rgba(46,168,223,0.16) 0%, transparent 60%), linear-gradient(145deg, #122b4a, #08101a)",
          }}
        />
        <div className="relative max-w-4xl mx-auto text-center space-y-8">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl font-extrabold uppercase text-white leading-tight">{h.ctaTitle}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base text-ops-muted max-w-2xl mx-auto leading-relaxed">{h.ctaText}</p>
          </Reveal>
          <Reveal delay={0.2} className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contato"
              data-testid="final-cta-contact"
              className="px-8 py-4 bg-ops-light text-ops-base text-xs font-display font-bold uppercase tracking-widest rounded hover:bg-white transition-colors"
            >
              {h.ctaBtn1}
            </Link>
            <Link
              to="/contato"
              data-testid="final-cta-presentation"
              className="px-8 py-4 border border-white/30 text-white text-xs font-display font-bold uppercase tracking-widest rounded hover:border-ops-light hover:text-ops-light transition-colors"
            >
              {h.ctaBtn2}
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
